<template>
  <div class="order-container">
    <!-- COLUNA PRINCIPAL -->
    <div class="order-main">
      <div class="card">
        <!-- HEADER -->
        <div class="card-header">
          <div>
            <h1>Detalhes da Encomenda</h1>
            <p class="order-subtitle">
              <span class="tag">#{{ order.id }}</span>
            </p>
          </div>

          <button
            class="btn btn-save"
            @click="saveAllChanges"
            :disabled="saving"
          >
            {{ saving ? 'A guardar...' : 'Guardar Alterações' }}
          </button>
        </div>

        <!-- SHIPPING -->
        <div class="shipping-box">
          <h2>Método de Envio</h2>

          <div class="form-group">
            <label>Nome do Envio</label>
            <input type="text" v-model="shippingName" disabled />
          </div>

          <div class="form-group">
            <label>Custo de Envio ({{ order.coin }})</label>
            <input
              type="number"
              min="0"
              step="0.01"
              v-model.number="shippingCost"
              @input="calcTotal"
            />
          </div>
        </div>

        <!-- HEADER ARTIGOS -->
        <div class="articles-header">
          <h2>Artigos</h2>
          <button class="btn btn-add" @click="openAddModal">+ Artigo</button>
        </div>

        <!-- LISTA ARTIGOS -->
        <ul class="article-list">
          <li
            v-for="article in order.articles"
            :key="article.id"
            :class="{ selected: selectedArticle?.id === article.id }"
            @click="selectArticle(article)"
          >
            <div class="article-main-line">
              <div>
                <strong>{{ article.quantity }}x [{{ article.sku }}]</strong>
                <span class="article-name">{{ article.name }}</span>
              </div>

              <div class="article-right">
                <span class="article-price">
                  {{ Number(article.total).toFixed(2) }} {{ order.coin }}
                </span>
                <button
                  class="btn btn-icon"
                  @click.stop="removeArticle(article)"
                >
                  ✕
                </button>
              </div>
            </div>
          </li>

          <li
            v-if="!order.articles || order.articles.length === 0"
            class="empty"
          >
            Nenhum artigo na encomenda.
          </li>
        </ul>

        <!-- FOOTER -->
        <div class="order-footer">
          <div class="footer-total">
            <span>Total atual</span>
            <span class="footer-total-value">
              {{ order.total }} {{ order.coin }}
            </span>
          </div>

          <button
            class="btn btn-save"
            @click="saveAllChanges"
            :disabled="saving"
          >
            {{ saving ? 'A guardar...' : 'Guardar Alterações' }}
          </button>
        </div>
      </div>
    </div>

    <!-- SIDEBAR -->
    <div class="order-sidebar" v-if="selectedArticle">
      <div class="card sidebar-card">
        <h3>Editar Artigo</h3>
        <p class="sidebar-article-name">{{ selectedArticle.name }}</p>
        <p class="sidebar-sku">
          SKU: <strong>{{ selectedArticle.sku }}</strong>
        </p>

        <div class="form-group">
          <label>Quantidade</label>
          <input type="number" v-model.number="editedQuantity" min="1" />
        </div>

        <div class="form-group">
          <label>Preço ({{ order.coin }})</label>
          <input
            type="number"
            v-model.number="editedPrice"
            min="0"
            step="0.01"
          />
        </div>

        <div class="sidebar-actions">
          <button class="btn btn-small" @click="updateArticleValues">
            Atualizar
          </button>
          <button
            class="btn btn-small btn-danger"
            @click="removeArticle(selectedArticle)"
          >
            Remover Artigo
          </button>
        </div>
      </div>
    </div>

    <!-- MODAL ADICIONAR ARTIGO -->
    <b-modal v-model="showModal" title="Adicionar Artigo" hide-footer>
      <b-form @submit.prevent="submitForm">
        <b-form-group label="SKU:">
          <b-form-input v-model="sku" required />
        </b-form-group>

        <b-form-group label="Quantidade:">
          <b-form-input
            v-model.number="prod_quantity"
            type="number"
            min="1"
            required
          />
        </b-form-group>

        <b-form-group>
          <template #label>Preço ({{ order.coin }})</template>
          <b-form-input
            v-model.number="prod_price"
            type="number"
            min="0"
            step="0.01"
            required
          />
        </b-form-group>

        <div class="modal-footer-actions">
          <b-button type="submit" variant="success">Adicionar</b-button>
          <b-button variant="secondary" @click="showModal = false"
            >Cancelar</b-button
          >
        </div>
      </b-form>
    </b-modal>
  </div>
</template>

<script>
import { mapGetters } from 'vuex/dist/vuex.common.js';

export default {
  name: 'OrderDetails',

  data() {
    return {
      order: { articles: [] },
      selectedArticle: null,
      editedQuantity: 1,
      editedPrice: 0,
      showModal: false,
      sku: '',
      prod_quantity: 1,
      prod_price: '',
      saving: false,

      /* SHIPPING */
      shippingName: '',
      shippingCost: 0,
    };
  },

  computed: {
    ...mapGetters(['getOrderId', 'getProductsSkuEan']),
  },

  created() {
    this.loadData();
  },

  methods: {
    async loadData() {
      await this.fetchOrder(this.$route.params.orderId);
      this.loadShipping();
      this.calcTotal();
    },

    /* ------------------ SHIPPING ------------------ */
    loadShipping() {
      try {
        const raw = this.order.shipping_lines?.[0];
        console.log('LOADING SHIPPING FROM RAW:', raw);
        if (!raw) return;

        console.log('RAW SHIPPING:', raw);

        console.log('Parsed SHIPPING:', JSON.parse(raw));
        const shipping = JSON.parse(raw)[0];

        this.shippingName = shipping.method_title || 'Shipping';
        this.shippingCost = Number(shipping.total) || 0;
      } catch (e) {
        console.log(e);
        this.shippingName = 'Shipping';
        this.shippingCost = 0;
      }
    },

    /* ------------------ ARTIGOS ------------------ */

    openAddModal() {
      this.sku = '';
      this.prod_quantity = 1;
      this.prod_price = '';
      this.showModal = true;
    },

    selectArticle(article) {
      this.selectedArticle = article;
      this.editedQuantity = article.quantity;
      this.editedPrice = Number(article.price);
    },

    async updateArticleValues() {
      if (!this.selectedArticle) return;

      const newLineTotal = this.editedQuantity * this.editedPrice;

      this.selectedArticle.quantity = this.editedQuantity;
      this.selectedArticle.price = this.editedPrice;
      this.selectedArticle.total = newLineTotal.toFixed(2);
      this.selectedArticle.subtotal = newLineTotal.toFixed(2);

      const idx = this.order.articles.findIndex(
        (a) => a.id === this.selectedArticle.id
      );
      if (idx !== -1) this.order.articles[idx] = this.selectedArticle;

      this.calcTotal();

      this.$swal({
        toast: true,
        icon: 'success',
        title: 'Artigo atualizado',
        timer: 1500,
      });
    },

    removeArticle(article) {
      this.order.articles = this.order.articles.filter(
        (a) => a.id !== article.id
      );

      if (this.selectedArticle?.id === article.id) {
        this.selectedArticle = null;
      }

      this.calcTotal();

      this.$swal({
        toast: true,
        icon: 'success',
        title: 'Artigo removido',
        timer: 1200,
      });
    },

    async submitForm() {
      await this.$store.dispatch('searchProductsEanSku', {
        search: this.sku,
      });

      const p = this.getProductsSkuEan[0];
      if (!p) {
        this.$swal({ icon: 'error', title: 'SKU não encontrado' });
        return;
      }

      const total = this.prod_price * this.prod_quantity;

      const article = {
        id: p.id_backoffice,
        name: p.name,
        sku: p.sku[0],
        quantity: this.prod_quantity,
        price: this.prod_price,
        total: total.toFixed(2),
        subtotal: total.toFixed(2),
      };

      this.order.articles.push(article);

      this.calcTotal();
      this.showModal = false;

      this.$swal({
        toast: true,
        icon: 'success',
        title: 'Artigo adicionado',
        timer: 1500,
      });
    },

    /* ------------------ TOTAL ------------------ */

    calcTotal() {
      const productsTotal = this.order.articles.reduce(
        (acc, a) => acc + Number(a.total || 0),
        0
      );

      this.order.total = (productsTotal + Number(this.shippingCost)).toFixed(2);
    },

    /* ------------------ SAVE ------------------ */

    async saveAllChanges() {
      try {
        this.saving = true;

        /** 1. OBTER O SHIPPING ORIGINAL COMO STRING */
        const raw = this.order.shipping_lines;

        /** 2. FAZER OS 2 PARSES */
        const shipping = JSON.parse(raw)[0]; // shipping verdadeiro

        /** 3. ALTERAR APENAS O QUE QUERES */
        shipping.total = String(Number(this.shippingCost).toFixed(2));
        shipping.method_title = this.shippingName;

        /** 5. PREPARAR PAYLOAD FINAL */
        const payload = {
          order_id: this.order.id,
          articles: this.order.articles,
          total: this.order.total,
          shipping: [shipping],
        };

        await this.$store.dispatch('modifyOrder', payload);

        this.$swal({
          toast: true,
          icon: 'success',
          title: 'Encomenda guardada com sucesso',
          timer: 1800,
        });
      } catch (e) {
        console.log('Erro ao montar shipping:', e);
        this.$swal({ icon: 'error', title: 'Erro ao atualizar shipping' });
      } finally {
        this.saving = false;
      }
    },

    /* ------------------ FETCH ------------------ */

    async fetchOrder(id) {
      await this.$store.dispatch('fetchOrderById', { id });
      const o = this.getOrderId.data[0];

      const articles = JSON.parse(o.articles[0] || '[]');

      this.order = { ...o, articles };
    },
  },
};
</script>

<style scoped>
.order-container {
  display: flex;
  max-width: 1600px;
  width: 100%;
  margin: 20px auto;
  padding: 0 30px;
  gap: 24px;
  font-family: Arial, sans-serif;
}

/* Colunas */
.order-main {
  flex: 3.2;
}

.order-sidebar {
  flex: 1;
  min-width: 280px !important;
}

/* Cards */
.card {
  background: white;
  border-radius: 12px;
  padding: 22px 26px;
  width: 100%;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

/* Header cards */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 16px;
  border-bottom: 1px solid #eee;
}

/* Resumo */
.order-summary {
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 12px 24px;
  margin: 20px 0;
}

/* Artigos */
.article-list {
  margin-top: 10px;
  padding: 0;
}

.article-list li {
  padding: 12px 14px;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 10px;
  background: #fff;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.article-list li.selected {
  background: #ffecec;
  border-color: #ffb1b1;
}

.article-main-line {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

/* Sidebar */
.sidebar-card {
  padding: 20px;
}

/* Footer */
.order-footer {
  margin-top: 20px;
  padding-top: 12px;
  display: flex;
  justify-content: space-between;
  border-top: 1px solid #eee;
}

/* Botões */
.btn {
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 13px;
  border: none;
  cursor: pointer;
}

.btn-save {
  background: #ff7979;
  color: white;
}

.btn-add {
  background: white;
  border: 1px solid #ff7979;
  color: #ff7979;
}

.btn-small {
  padding: 6px 12px;
}

.shipping-box {
  background: #fff7f7;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 22px;
}

.shipping-box h2 {
  font-size: 16px;
  margin-bottom: 12px;
}

.shipping-box input {
  width: 200px;
  padding: 7px 10px;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 13px;
}
</style>
